import { Component } from 'react';
import { AlertCircle, RefreshCw, Home, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import * as Sentry from '@sentry/react';
import logger from '../../utils/logger';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('ErrorBoundary caught error', {
      error,
      errorInfo,
      boundaryName: this.props.name || 'unknown'
    });

    // Log to Sentry if available
    if (Sentry && Sentry.captureException) {
      Sentry.withScope((scope) => {
        scope.setTag('error_boundary', this.props.name || 'unknown');
        scope.setLevel('error');
        scope.setContext('errorInfo', errorInfo);
        scope.setContext('componentStack', {
          componentStack: errorInfo.componentStack
        });

        const eventId = Sentry.captureException(error);
        this.setState({ eventId, errorInfo });
      });
    } else {
      this.setState({ errorInfo });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      showDetails: false
    });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  handleReportFeedback = () => {
    if (this.state.eventId && Sentry && Sentry.showReportDialog) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, eventId, showDetails } = this.state;
      const {
        fallbackTitle,
        fallbackMessage,
        showReset = true,
        showHome = true,
        showFeedback = true,
        level = 'error' // 'error', 'warning', 'info'
      } = this.props;

      // Determine styling based on level
      const levelStyles = {
        error: {
          bg: 'bg-red-50 dark:bg-red-900/10',
          border: 'border-red-200 dark:border-red-800',
          icon: 'text-red-500 dark:text-red-400',
          button: 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600'
        },
        warning: {
          bg: 'bg-yellow-50 dark:bg-yellow-900/10',
          border: 'border-yellow-200 dark:border-yellow-800',
          icon: 'text-yellow-500 dark:text-yellow-400',
          button: 'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-600'
        },
        info: {
          bg: 'bg-brand-50 dark:bg-brand-900/10',
          border: 'border-brand-200 dark:border-brand-800',
          icon: 'text-brand-500 dark:text-brand-400',
          button: 'bg-brand-500 hover:bg-brand-600 dark:bg-brand-500 dark:hover:bg-brand-500'
        }
      };

      const styles = levelStyles[level] || levelStyles.error;

      return (
        <div
          className={`flex flex-col items-center justify-center p-6 ${styles.bg} border ${styles.border} rounded-xl`}
          role="alert"
          aria-live="assertive"
        >
          <AlertCircle className={`${styles.icon} mb-3`} size={40} aria-hidden="true" />

          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {fallbackTitle || 'Something went wrong'}
          </h3>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center max-w-md">
            {fallbackMessage || 'We encountered an unexpected error. Please try again or contact support if the problem persists.'}
          </p>

          {/* Error Details Toggle */}
          {error && (
            <button
              onClick={this.toggleDetails}
              className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
              aria-expanded={showDetails}
            >
              {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showDetails ? 'Hide' : 'Show'} technical details
            </button>
          )}

          {/* Error Details */}
          {showDetails && error && (
            <div className="w-full max-w-2xl mb-4 p-4 bg-gray-900 dark:bg-gray-950 rounded-lg overflow-auto max-h-48">
              <p className="text-xs font-mono text-red-400 mb-2">
                <strong>Error:</strong> {error.toString()}
              </p>
              {errorInfo && errorInfo.componentStack && (
                <pre className="text-xs font-mono text-gray-400 whitespace-pre-wrap">
                  {errorInfo.componentStack}
                </pre>
              )}
              {eventId && (
                <p className="text-xs text-gray-500 mt-2">
                  Event ID: {eventId}
                </p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {showReset && (
              <button
                onClick={this.handleReset}
                className={`flex items-center gap-2 px-4 py-2 ${styles.button} text-white rounded-lg transition-colors`}
                aria-label="Try again"
              >
                <RefreshCw size={16} aria-hidden="true" />
                Try Again
              </button>
            )}

            {showHome && (
              <button
                onClick={this.handleGoHome}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-dark-700 dark:hover:bg-gray-600 text-white rounded-lg transition-colors"
                aria-label="Go to dashboard"
              >
                <Home size={16} aria-hidden="true" />
                Go to Dashboard
              </button>
            )}

            {showFeedback && eventId && (
              <button
                onClick={this.handleReportFeedback}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-dark-800 dark:hover:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                aria-label="Report this error"
              >
                <Mail size={16} aria-hidden="true" />
                Report Issue
              </button>
            )}
          </div>

          {/* Help Text */}
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-4 text-center max-w-md">
            If this problem continues, please contact support with the error details above.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
